import type { PowerDistributionConfigurationsSaveHandle } from './subsections/powerDistributionConfigurationsSaveHandle';
import type { SelectedFile } from '../../../components/forms/FileUpload';
import type { RepeatableInstanceSaveHandle } from '../../../components/repeatableSections/repeatableInstanceSaveHandle';
import type { SavedAttachment } from '../../../services/attachmentService';
import type { Request } from '../../../types';
import type { PowerDistributionConfiguration } from '../../../types/powerDistribution';

export interface PowerDistributionInitialProps {
  formData: Record<string, unknown> & {
    stage?: string;
    sysRequestStage?: string;
  };
  updateFormData: (
    field: string,
    value:
      | string
      | number
      | boolean
      | PowerDistributionConfiguration[]
      | undefined,
  ) => void;
  requestId?: string;
  disabled?: boolean;
  attachmentsBySection?: Record<string, SavedAttachment[]>;
  onAttachFiles?: (sectionId: string) => void;
  onDeleteAttachment?: (attachmentId: number) => void;
  isEditing?: boolean;
  selectedFiles?: Record<string, SelectedFile[]>;
  onFilesSelected?: (sectionId: string, files: SelectedFile[]) => void;
  offlineMode?: boolean;
  showFullDetails?: boolean;
  enabledStepIds?: readonly string[];
  registerPowerDistributionSaveHandle?: (
    handle: PowerDistributionConfigurationsSaveHandle | null,
  ) => void;
  onPowerDistributionEditorActiveChange?: (active: boolean) => void;
}

export interface PowerDistributionPersistedProps {
  request: Request;
  isEditing: boolean;
  editedData: Record<string, unknown>;
  updateEditedData: (
    field: string,
    value: string | number | boolean | undefined,
  ) => void;
  showFullDetails?: boolean;
  showViewToggle?: boolean;
  isAdvancedView?: boolean;
  onToggleView?: () => void;
  attachmentsBySection?: Record<string, SavedAttachment[]>;
  onAttachFiles?: (sectionId: string) => void;
  onDeleteAttachment?: (attachmentId: number) => void;
  selectedFiles?: Record<string, SelectedFile[]>;
  onFilesSelected?: (sectionId: string, files: SelectedFile[]) => void;
  offlineMode?: boolean;
  onCaptionChange?: (attachmentId: number, caption: string) => void;
  pendingCaptionEdits?: Record<number, string>;
  enabledStepIds?: readonly string[];
  /**
   * Request Detail engine wiring (#997 Story 6.7). The engine section registers
   * its save handle with the screen's `usePowerDistributionEngineSaveBridge` and
   * writes the live session list into `editedData.powerDistributionConfigurations`
   * via `applyEditedDataPatch` so it survives step navigation.
   */
  registerInstanceSaveHandle?: (
    handle: RepeatableInstanceSaveHandle | null,
  ) => void;
  applyEditedDataPatch?: (patch: Record<string, unknown>) => void;
  /** True when `editedData.powerDistributionConfigurations` was touched this edit session. */
  powerDistributionConfigurationsDirty?: boolean;
  onPowerDistributionEditorActiveChange?: (active: boolean) => void;
}

export type PowerDistributionProps =
  | PowerDistributionInitialProps
  | PowerDistributionPersistedProps;

export function isPowerDistributionPersistedProps(
  p: PowerDistributionProps,
): p is PowerDistributionPersistedProps {
  return (
    'request' in p && (p as PowerDistributionPersistedProps).request != null
  );
}
